import { useRef, useState, useEffect } from 'react';
import Button from 'react-bootstrap/Button';
import { IoSearch, IoCloseCircleOutline } from 'react-icons/io5';

import { useProject } from '@contexts/Project.context';
import { useAuth } from '@contexts/User.context';
import { useToast } from '@contexts/Toast.context';
import { useConfirm } from '@contexts/Confirm.context';

import useNotify from '@hooks/useNotify';

import {
	setupFreehandDraw,
	removeFreehandDraw,
} from '@utils/map/freeHand.draw';
import { setLayersVisibilityByCustomIds } from '@utils/map/helpers';
import { detectObjects } from '@utils/api';

// TODO: We will be moving away from selecting object_detection or segmentation on model creation and instead let
// the user select model when clicking the 'Detect Objects' button.

/**
 * Component to draw a selection area and run detection on the selected area
 *
 * @returns the button to draw the selection area
 */
const DetectInArea = ({ isTrained }) => {
	const {
		tierTrial,
		trialEnded,
		isEnterprise,
		activeOrgAccess,
		tierPremium,
		tierPro,
		userAccess,
		subscriptionActive,
	} = useAuth();

	const {
		project,
		pickedTask,
		toolBarVisible,
		mapObject,
		isDemo,
		selectedSingleImage,
		dispatch,
	} = useProject();

	const { uuid: projectUuid } = project ?? {};
	const { model_uuid: modelUuid } = pickedTask ?? {};

	const { addToast } = useToast();
	const { confirm } = useConfirm();
	const { checkForNotifications } = useNotify();

	const drawRef = useRef(null);
	const [isDrawing, setIsDrawing] = useState(false);

	const [layersToShow, setLayersToShow] = useState([]);
	const layersToHide = ['hexagonLayer'];

	const allowedToDetect = async () => {
		const showAlert = msg => {
			addToast({
				id: 'detect-permission-denied',
				bg: 'warning',
				title: msg,
			});
			return false;
		};

		if (tierTrial && trialEnded)
			return showAlert(
				'Your trial has ended and you can no longer detect objects'
			);

		if (tierPro && !subscriptionActive)
			return showAlert(
				'Your user needs an active subscription to detect objects'
			);

		if (tierPremium && !userAccess?.includes('detect'))
			return showAlert(
				'Your user does not have access rights to detect objects'
			);

		if (isEnterprise) {
			const hasOrgAccess =
				activeOrgAccess.includes('all') ||
				activeOrgAccess.includes('detect');

			if (!hasOrgAccess)
				return showAlert(
					'Your user does not have access rights to detect objects'
				);
		}

		return true; // Allow detection if none of the conditions are met
	};

	const getLayersToShow = () => {
		const layers = mapObject.getLayers()?.getArray();
		if (!layers?.length) return [];

		// Gets an array of customLayerId strings that are visible
		return layers.reduce((acc, layer) => {
			const { customLayerId, visible } = layer?.getProperties() ?? {};
			if (customLayerId && visible) acc.push(customLayerId);
			return acc;
		}, []);
	};

	const initDetectionFlow = async () => {
		const allowed = await allowedToDetect();
		if (!allowed) return resetDetectionFlow();

		dispatch({
			type: 'setDialogue',
			payload: {
				header: 'Set a boundary',
				body: (
					<p>
						Please draw a boundary around the region where you wish
						to generate detections. Press ESC to cancel.
					</p>
				),
				fullWidth: false,
				autoClose: true,
			},
		});

		dispatch({ type: 'setToolBarVisible', payload: false });
		dispatch({
			type: 'setInteractionsDisabled',
			payload: true,
		});

		setIsDrawing(true);
		const existingLayersVisibility = getLayersToShow();
		setLayersToShow(existingLayersVisibility);

		// Hide all layers that should not be visible during drawing
		setLayersVisibilityByCustomIds(mapObject, layersToHide, false);
	};

	const drawSelection = () => {
		// Setup draw interaction
		const { draw, layer } = setupFreehandDraw({
			type: 'Polygon',
			mapRef: mapObject,
			freehand: false,
		});
		drawRef.current = { draw, layer };

		draw.on('drawend', async e => {
			setIsDrawing(false);
			const coordinates = e.feature.getGeometry().getCoordinates();

			const userConfirmed = await confirm({
				title: 'Confirm Selection',
				message: (
					<>
						<p>
							Detecting objects is a resource-intensive process that may take several
							minutes, depending on the area selected.
						</p>
						<p>Do you want to run detection in the selected area?</p>
					</>
				),
			});

			if (userConfirmed) await runDetection(coordinates[0]);
			else resetDetectionFlow();
		});
	};

	const resetDetectionFlow = () => {
		dispatch({ type: 'setDialogue', payload: null });
		dispatch({ type: 'setToolBarVisible', payload: true });
		dispatch({
			type: 'setInteractionsDisabled',
			payload: false,
		});

		setIsDrawing(false);
		setLayersVisibilityByCustomIds(mapObject, layersToShow, true);

		const { draw, layer } = drawRef?.current ?? {};
		if (!draw || !layer) return;

		removeFreehandDraw({
			mapRef: mapObject,
			draw,
			layer,
		});
	};

	const runDetection = async polygon => {
		try {
			const response = await detectObjects({
				projectUuid,
				modelUuid,
				polygon,
			});

			const message = `
				Detection is running. Depending on the area
				selected, this may take several minutes. You
				will be notified when detection is complete.
			`;

			const { warning } = response ?? {};
			if (warning) {
				addToast({
					id: 'detection-warning',
					className: 'bg-warning',
					title: 'Detection started with warnings:',
					message: (
						<>
							<p>{warning}.</p>
							<p className="mb-0">{message}</p>
						</>
					),
					autohide: true,
				});
			} else
				addToast({
					id: 'detection-success',
					className: 'bg-success',
					title: 'Running detection...',
					message,
					autohide: true,
				});
		} catch (error) {
			addToast({
				id: 'detection-error',
				className: 'bg-danger',
				title: 'Error Running detection...',
				message:
					error?.response?.data?.detail ??
					error?.message ??
					'Unknown error running detection.',
				autohide: false,
			});
		} finally {
			resetDetectionFlow();
			checkForNotifications();
		}
	};

	useEffect(() => {
		if (!isDrawing) return;
		drawSelection(); // Start drawing selection

		const handleKeyUp = ({ key }) =>
			key == 'Escape' && !toolBarVisible && resetDetectionFlow();

		window.addEventListener('keyup', handleKeyUp);
		return () => {
			window.removeEventListener('keyup', handleKeyUp);
		};
	}, [isDrawing]);

	if (!modelUuid || selectedSingleImage) return null;
	return (
		<>
			{isDrawing ? (
				<Button
					disabled={toolBarVisible || isDemo}
					onClick={resetDetectionFlow}
					variant="dark-secondary"
					className="btn-dark secondary"
					data-tooltip-id="tooltip-toolbar-root"
					data-tooltip-content="Cancel detection mode"
					data-tooltip-place="top">
					<IoCloseCircleOutline className="m-1 mb-2" />
					Cancel Detection
				</Button>
			) : (
				<Button
					disabled={!isTrained || !toolBarVisible || isDemo}
					onClick={initDetectionFlow}
					data-tooltip-id="tooltip-toolbar-root"
					data-tooltip-content={
						tierTrial && trialEnded
							? 'Your trial has ended'
							: 'Detect objects in a selected area'
					}
					data-tooltip-place="top"
					className="btn-dark secondary">
					<IoSearch className="m-1 mb-2" />
					Detect Objects
				</Button>
			)}
		</>
	);
};

export default DetectInArea;
